import React from 'react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface SecuritySettingsData {
  twoFactorAuth: boolean;
  ssoEnabled: boolean;
  passwordMinLength: number;
  passwordRequireSpecialChars: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireUppercase: boolean;
  sessionTimeout: number;
  ipWhitelisting: boolean;
  allowedIPs: string;
  loginAttempts: number;
  lockoutDuration: number;
  encryptionLevel: 'standard' | 'enhanced' | 'maximum';
}

interface ISecuritySettings {
  onSave?: (data: SecuritySettingsData) => Promise<void> | void;
}

export const SecuritySettings: React.FC<ISecuritySettings> = ({ onSave }) => {
  const initialData: SecuritySettingsData = {
    twoFactorAuth: true,
    ssoEnabled: false,
    passwordMinLength: 8,
    passwordRequireSpecialChars: true,
    passwordRequireNumbers: true,
    passwordRequireUppercase: true,
    sessionTimeout: 30,
    ipWhitelisting: false,
    allowedIPs: '192.168.1.0/24\n10.0.0.0/8',
    loginAttempts: 5,
    lockoutDuration: 15,
    encryptionLevel: 'enhanced',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  const icon = (
    <svg className='w-5 h-5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
    </svg>
  );

  const handleToggleChange = (field: keyof SecuritySettingsData) => {
    handleInputChange(field, !formData[field]);
  };

  const getEncryptionDisplayText = (level: string) => {
    switch (level) {
      case 'standard': return 'Standard (AES-128)';
      case 'enhanced': return 'Enhanced (AES-256)';
      case 'maximum': return 'Maximum (AES-256 + HSM)';
      default: return level;
    }
  };

  return (
    <SettingsCard
        title='Security Settings'
        description='Configure security policies and authentication requirements'
        icon={icon}
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={handleEditToggle}
        onSave={handleSave}
    >
      { isEditing ? (
        // Edit Mode
        <>
          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Authentication</h3>
            <div className='space-y-4'>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.twoFactorAuth}
                    onChange={() => handleToggleChange('twoFactorAuth')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Two-Factor Authentication (2FA)</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.ssoEnabled}
                    onChange={() => handleToggleChange('ssoEnabled')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Single Sign-On (SSO)</span>
              </label>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Password Policy</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-4'>
              <FloatingLabelInput
                  label='Minimum Password Length'
                  type='number'
                  value={formData.passwordMinLength.toString()}
                  onChange={(e) => handleInputChange('passwordMinLength', parseInt(e.target.value) || 8)}
              />
            </div>
            <div className='space-y-4'>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.passwordRequireSpecialChars}
                    onChange={() => handleToggleChange('passwordRequireSpecialChars')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Require Special Characters</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.passwordRequireNumbers}
                    onChange={() => handleToggleChange('passwordRequireNumbers')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Require Numbers</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.passwordRequireUppercase}
                    onChange={() => handleToggleChange('passwordRequireUppercase')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Require Uppercase Letters</span>
              </label>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Session & Access Control</h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-4'>
              <FloatingLabelInput
                  label='Session Timeout (minutes)'
                  type='number'
                  value={formData.sessionTimeout.toString()}
                  onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value) || 30)}
              />
              <FloatingLabelInput
                  label='Max Login Attempts'
                  type='number'
                  value={formData.loginAttempts.toString()}
                  onChange={(e) => handleInputChange('loginAttempts', parseInt(e.target.value) || 5)}
              />
              <FloatingLabelInput
                  label='Lockout Duration (minutes)'
                  type='number'
                  value={formData.lockoutDuration.toString()}
                  onChange={(e) => handleInputChange('lockoutDuration', parseInt(e.target.value) || 15)}
              />
            </div>
            <label className='flex items-center'>
              <input
                  type='checkbox'
                  checked={formData.ipWhitelisting}
                  onChange={() => handleToggleChange('ipWhitelisting')}
                  className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
              />
              <span className='ml-3 text-sm text-gray-700'>Enable IP Whitelisting</span>
            </label>
            { formData.ipWhitelisting && (
              <div className='mt-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Allowed IP Addresses/Ranges</label>
                <textarea
                    value={formData.allowedIPs}
                    onChange={(e) => handleInputChange('allowedIPs', e.target.value)}
                    rows={4}
                    className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                    placeholder='192.168.1.0/24&#10;10.0.0.0/8&#10;203.0.113.5'
                />
                <p className='text-xs text-gray-500 mt-1'>Enter one IP address or CIDR range per line</p>
              </div>
            ) }
          </div>

          <div>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Encryption</h3>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Encryption Level</label>
              <select
                  value={formData.encryptionLevel}
                  onChange={(e) => handleInputChange('encryptionLevel', e.target.value)}
                  className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
              >
                <option value='standard'>Standard (AES-128)</option>
                <option value='enhanced'>Enhanced (AES-256)</option>
                <option value='maximum'>Maximum (AES-256 + HSM)</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        // View Mode
        <>
          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Authentication Status</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='flex items-center p-3 bg-gray-50 rounded-lg'>
                <div className={`w-3 h-3 rounded-full mr-3 ${formData.twoFactorAuth ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className='text-sm text-gray-700'>Two-Factor Authentication</span>
                <span className={`ml-auto text-xs px-2 py-1 rounded-full ${formData.twoFactorAuth ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  { formData.twoFactorAuth ? 'Enabled' : 'Disabled' }
                </span>
              </div>
              <div className='flex items-center p-3 bg-gray-50 rounded-lg'>
                <div className={`w-3 h-3 rounded-full mr-3 ${formData.ssoEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className='text-sm text-gray-700'>Single Sign-On</span>
                <span className={`ml-auto text-xs px-2 py-1 rounded-full ${formData.ssoEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  { formData.ssoEnabled ? 'Enabled' : 'Disabled' }
                </span>
              </div>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Password Policy</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='Minimum Length'
                    text={`${formData.passwordMinLength} characters`}
                />
              </div>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='Requirements'
                    text={[
                      formData.passwordRequireSpecialChars && 'Special chars',
                      formData.passwordRequireNumbers && 'Numbers',
                      formData.passwordRequireUppercase && 'Uppercase',
                    ].filter(Boolean).join(', ') || 'None'}
                />
              </div>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Session & Access Control</h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='Session Timeout'
                    text={`${formData.sessionTimeout} minutes`}
                />
              </div>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='Max Login Attempts'
                    text={formData.loginAttempts.toString()}
                />
              </div>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='Lockout Duration'
                    text={`${formData.lockoutDuration} minutes`}
                />
              </div>
            </div>
            <div className='mt-4'>
              <div className='flex items-center p-3 bg-gray-50 rounded-lg'>
                <div className={`w-3 h-3 rounded-full mr-3 ${formData.ipWhitelisting ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className='text-sm text-gray-700'>IP Whitelisting</span>
                <span className={`ml-auto text-xs px-2 py-1 rounded-full ${formData.ipWhitelisting ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  { formData.ipWhitelisting ? 'Active' : 'Inactive' }
                </span>
              </div>
              { formData.ipWhitelisting && (
                <div className='mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200'>
                  <DetailedItem
                      label='Allowed IP Ranges'
                      text={formData.allowedIPs.split('\n').join(', ')}
                      helpertext='Only these IP addresses can access the system'
                  />
                </div>
              ) }
            </div>
          </div>

          <div>
            <div className='p-4 bg-green-50 rounded-lg border border-green-200'>
              <DetailedItem
                  label='Encryption Level'
                  text={getEncryptionDisplayText(formData.encryptionLevel)}
                  helpertext='All data is encrypted using industry-standard protocols'
              />
            </div>
          </div>
        </>
      ) }
    </SettingsCard>
  );
};
